import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import './AuthPage.css';

const AuthPage = ({ onAuthSuccess }) => {
  const { signup, login, verifyOTP, resendOTP, authError, clearError } = useAuth();

  // Auth state
  const [mode, setMode] = useState('login'); // 'login', 'signup', 'otp'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
  const [otpType, setOtpType] = useState('login');
  const [isVerified, setIsVerified] = useState(false);

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [canResend, setCanResend] = useState(true);
  const [resendTimer, setResendTimer] = useState(0);

  const otpRefs = useRef([]);

  // Clear errors when mode changes
  useEffect(() => {
    setError('');
    setSuccessMessage('');
    clearError();
  }, [mode, clearError]);

  // Resend timer
  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    } else {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  // Handle OTP input
  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // Only allow digits

    const newOtp = [...otpCode];
    newOtp[index] = value.slice(-1); // Only keep last digit
    setOtpCode(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newOtp = [...otpCode];
    for (let i = 0; i < pastedData.length; i++) {
      newOtp[i] = pastedData[i];
    }
    setOtpCode(newOtp);
    if (pastedData.length === 6) {
      otpRefs.current[5]?.focus();
    }
  };

  // Handle signup
  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);
    try {
      const result = await signup(email, password, name);
      if (result.success && result.requires_otp) {
        setOtpType('verification');
        setIsVerified(false);
        setMode('otp');
        setSuccessMessage('Verification code sent to your email');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle login
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    setIsLoading(true);
    try {
      const result = await login(email, password);
      if (result.success && result.requires_otp) {
        setIsVerified(result.user?.is_verified || false);
        setOtpType(result.user?.is_verified ? 'login' : 'verification');
        setMode('otp');
        setSuccessMessage(result.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle OTP verification
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');

    const code = otpCode.join('');
    if (code.length !== 6) {
      setError('Please enter complete 6-digit code');
      return;
    }

    setIsLoading(true);
    try {
      const result = await verifyOTP(email, code, otpType);

      if (result.success) {
        if (result.session_token) {
          // Login complete
          setSuccessMessage('Login successful!');
          setTimeout(() => {
            onAuthSuccess && onAuthSuccess();
          }, 500);
        } else if (result.requires_otp) {
          // Email verified, now need login OTP
          setOtpType('login');
          setOtpCode(['', '', '', '', '', '']);
          setSuccessMessage(result.message);
        }
      }
    } catch (err) {
      setError(err.message);
      setOtpCode(['', '', '', '', '', '']);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle resend OTP
  const handleResendOTP = async () => {
    if (!canResend) return;

    setCanResend(false);
    setResendTimer(30);

    try {
      await resendOTP(email, otpType);
      setSuccessMessage('New code sent to your email');
    } catch (err) {
      setError(err.message);
    }
  };

  // Render OTP form
  const renderOTPForm = () => (
    <form className="auth-form" onSubmit={handleVerifyOTP}>
      <div className="otp-header">
        <h2>{otpType === 'verification' ? 'Verify Your Email' : 'Enter Login Code'}</h2>
        <p className="otp-subtitle">
          We sent a 6-digit code to <strong>{email}</strong>
        </p>
      </div>

      <div className="otp-inputs">
        {otpCode.map((digit, index) => (
          <input
            key={index}
            ref={(el) => (otpRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            maxLength="1"
            value={digit}
            onChange={(e) => handleOtpChange(index, e.target.value)}
            onKeyDown={(e) => handleOtpKeyDown(index, e)}
            onPaste={handleOtpPaste}
            className="otp-input"
            autoFocus={index === 0}
          />
        ))}
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      <button type="submit" className="auth-button" disabled={isLoading}>
        {isLoading ? (
          <span className="loading-spinner"></span>
        ) : (
          'Verify Code'
        )}
      </button>

      <div className="resend-section">
        <p className="resend-text">
          Didn't receive the code?{' '}
          {canResend ? (
            <button type="button" onClick={handleResendOTP} className="resend-button">
              Resend
            </button>
          ) : (
            <span className="resend-timer">Resend in {resendTimer}s</span>
          )}
        </p>
      </div>

      <button
        type="button"
        className="back-button"
        onClick={() => {
          setMode('login');
          setOtpCode(['', '', '', '', '', '']);
        }}
      >
        Back to Login
      </button>
    </form>
  );

  // Render login form
  const renderLoginForm = () => (
    <form className="auth-form" onSubmit={handleLogin}>
      <h2>Welcome Back</h2>

      <div className="form-group">
        <label htmlFor="email">Email</label>
        <input
          type="email"
          id="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          required
          autoFocus
        />
      </div>

      <div className="form-group">
        <label htmlFor="password">Password</label>
        <input
          type="password"
          id="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Enter your password"
          required
        />
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      <button type="submit" className="auth-button" disabled={isLoading}>
        {isLoading ? (
          <span className="loading-spinner"></span>
        ) : (
          'Continue'
        )}
      </button>

      <div className="auth-switch">
        <p>
          Don't have an account?{' '}
          <button type="button" onClick={() => setMode('signup')}>
            Sign up
          </button>
        </p>
      </div>
    </form>
  );

  // Render signup form
  const renderSignupForm = () => (
    <form className="auth-form" onSubmit={handleSignup}>
      <h2>Create Account</h2>

      <div className="form-group">
        <label htmlFor="name">Name (optional)</label>
        <input
          type="text"
          id="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name"
        />
      </div>

      <div className="form-group">
        <label htmlFor="signup-email">Email</label>
        <input
          type="email"
          id="signup-email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          required
          autoFocus
        />
      </div>

      <div className="form-group">
        <label htmlFor="signup-password">Password</label>
        <input
          type="password"
          id="signup-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="At least 8 characters"
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="confirm-password">Confirm Password</label>
        <input
          type="password"
          id="confirm-password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="Confirm your password"
          required
        />
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      <button type="submit" className="auth-button" disabled={isLoading}>
        {isLoading ? (
          <span className="loading-spinner"></span>
        ) : (
          'Create Account'
        )}
      </button>

      <div className="auth-switch">
        <p>
          Already have an account?{' '}
          <button type="button" onClick={() => setMode('login')}>
            Sign in
          </button>
        </p>
      </div>
    </form>
  );

  return (
    <div className="auth-page">
      <div className="auth-container">
        {/* Logo and branding */}
        <div className="auth-branding">
          <img
            src="/allocq-logo-dark.svg"
            alt="AllocQ"
            className="auth-logo-lockup"
          />
          <p className="auth-subtitle">AI-Powered Portfolio Allocation</p>
        </div>

        {/* Auth forms */}
        <div className="auth-forms">
          {mode === 'otp' && renderOTPForm()}
          {mode === 'login' && renderLoginForm()}
          {mode === 'signup' && renderSignupForm()}
        </div>

        {/* Footer */}
        <div className="auth-footer">
          <p>Secure 2-step authentication</p>
        </div>
      </div>

      {/* Background decoration */}
      <div className="auth-bg">
        <div className="auth-orb auth-orb-1"></div>
        <div className="auth-orb auth-orb-2"></div>
        <div className="auth-orb auth-orb-3"></div>
      </div>
    </div>
  );
};

export default AuthPage;
